// Releases / Profiles / :releaseId inspection page.
//
// Per-release inspection: file count, OS rollup, top GPUs/models, list of
// workers currently holding this version. Reuses the same MajorBreakdown
// renderer the pre-publish preview card uses so the operator sees an
// identical "what's in this bundle" view across the publish flow and the
// catalog view.
//
// Data source: GET /api/releases/profiles/:id which joins ProfileRelease
// with its source RawBundle's cached detectedMajors. Falls back gracefully
// when the bundle row is missing (legacy releases) so the operator still
// sees the OS rollup from the ProfileRelease.oses summary.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import {
	AppWindow,
	Boxes,
	CheckCircle2,
	ChevronLeft,
	Download,
	Loader2,
	Trash2,
} from "lucide-react";
import { lazy, Suspense, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";
import {
	type DetectedMajor,
	MajorBreakdown,
} from "@/components/profile-major-card";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";

const DeleteReleaseDialog = lazy(
	() => import("@/components/delete-release-dialog"),
);

interface ReleaseDetail {
	id: string;
	browserMajor: number;
	version: string;
	slug: string;
	description: string | null;
	publishedAt: string;
	publishedBy: string | null;
	sha256: string;
	sizeBytes: number;
	fileCount: number;
	oses: { win: number; mac: number; linux: number; android: number };
	webPath: string;
	isActive: boolean;
	rawBundleId: string | null;
	rawBundleFilename: string | null;
	detectedMajors: DetectedMajor[] | null;
	inspectionError: string | null;
}

interface ReleaseDetailResponse {
	success: boolean;
	release: ReleaseDetail;
}

interface FleetSnapshotMaybe {
	hostname: string;
	installedProfiles: Record<string, string[]> | null;
}

interface FleetResponse {
	success: boolean;
	hosts?: Array<{ hostname: string; snapshot: FleetSnapshotMaybe | null }>;
	snapshots?: FleetSnapshotMaybe[];
}

export function ProfileReleaseDetail({ releaseId }: { releaseId: string }) {
	const { user } = useAuth();
	const canPublish = hasRole(user, "operator");
	const queryClient = useQueryClient();
	const [deleteOpen, setDeleteOpen] = useState(false);

	const detailQuery = useQuery({
		queryKey: ["profile-release", releaseId],
		queryFn: async () => {
			const res = await fetch(
				`/api/releases/profiles/${encodeURIComponent(releaseId)}`,
				{ credentials: "same-origin" },
			);
			const data = await res.json();
			if (!res.ok) {
				throw new Error(data?.error?.message ?? `release ${res.status}`);
			}
			return data as ReleaseDetailResponse;
		},
	});

	const fleetQuery = useQuery({
		queryKey: ["workers"],
		queryFn: () => unwrap(api.api.workers.get()) as Promise<FleetResponse>,
		refetchInterval: 60_000,
	});

	const activate = useMutation({
		mutationFn: async () => {
			const res = await fetch(
				`/api/releases/profiles/${encodeURIComponent(releaseId)}/activate`,
				{ method: "POST", credentials: "same-origin" },
			);
			const data = await res.json();
			if (!res.ok) {
				throw new Error(data?.error?.message ?? `activate ${res.status}`);
			}
			return data;
		},
		onSuccess: () => {
			toast.success("Activated");
			queryClient.invalidateQueries({
				queryKey: ["profile-release", releaseId],
			});
			queryClient.invalidateQueries({ queryKey: ["profile-releases"] });
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Activate failed"),
	});

	const remove = useMutation({
		mutationFn: async (force: boolean) => {
			const qs = force ? "?force=1" : "";
			const res = await fetch(
				`/api/releases/profiles/${encodeURIComponent(releaseId)}${qs}`,
				{ method: "DELETE", credentials: "same-origin" },
			);
			const data = await res.json();
			if (!res.ok) {
				throw new Error(data?.error?.message ?? `delete ${res.status}`);
			}
			return data;
		},
		onSuccess: () => {
			toast.success("Deleted");
			queryClient.invalidateQueries({ queryKey: ["profile-releases"] });
			queryClient.invalidateQueries({ queryKey: ["manifest"] });
			setDeleteOpen(false);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Delete failed"),
	});

	const release = detailQuery.data?.release;

	const installedHosts = useMemo(() => {
		if (!release) return [] as string[];
		const out: string[] = [];
		const snapshots =
			fleetQuery.data?.hosts
				?.map((h) => h.snapshot)
				.filter((s): s is FleetSnapshotMaybe => s != null) ??
			fleetQuery.data?.snapshots ??
			[];
		const versionedSlug = `${release.version}-${release.slug}`;
		for (const s of snapshots) {
			const versions =
				s.installedProfiles?.[String(release.browserMajor)] ?? [];
			const stripped = versions.map((v) =>
				v.startsWith("v") ? v.slice(1) : v,
			);
			if (
				stripped.includes(versionedSlug) ||
				stripped.includes(release.version)
			) {
				out.push(s.hostname);
			}
		}
		return out;
	}, [fleetQuery.data, release]);

	if (detailQuery.isPending) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-32" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	if (detailQuery.isError || !release) {
		return (
			<div className="space-y-6">
				<Button asChild variant="ghost" size="sm">
					<Link href="/releases/profiles">
						<ChevronLeft className="mr-1 size-3.5" /> Back to profiles
					</Link>
				</Button>
				<Card>
					<CardContent className="p-6 text-sm text-destructive">
						{detailQuery.error instanceof Error
							? detailQuery.error.message
							: "Release not found."}
					</CardContent>
				</Card>
			</div>
		);
	}

	const detected = release.detectedMajors ?? [];
	const matchingMajor = detected.find((d) => d.major === release.browserMajor);
	// Overlay published OS counts and file total onto the raw-detected major so
	// the OS pie shows the real published distribution (e.g. Android=2047)
	// rather than what the detector found in the raw bundle.
	const breakdownMajor = matchingMajor
		? { ...matchingMajor, oses: release.oses, fileCount: release.fileCount }
		: undefined;

	return (
		<div className="space-y-6">
			<div>
				<div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
					<Link href="/releases" className="hover:underline">
						Releases
					</Link>
					<span>›</span>
					<Link href="/releases/profiles" className="hover:underline">
						Profiles
					</Link>
					<span>›</span>
					<Link
						href={`/releases/chromium/${release.browserMajor}`}
						className="hover:underline"
					>
						Chromium {release.browserMajor}
					</Link>
					<span>›</span>
					<span className="font-mono">
						{release.version} {release.slug}
					</span>
				</div>

				<div className="flex flex-wrap items-end justify-between gap-3">
					<div>
						<div className="flex flex-wrap items-center gap-2">
							<div className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium">
								<Boxes className="size-4 text-muted-foreground" />
								<span>Profile bundle</span>
								<span className="font-mono">{release.version}</span>
								<Badge variant="outline" className="font-mono">
									{release.slug}
								</Badge>
								{release.isActive ? (
									<Badge variant="success">active</Badge>
								) : null}
							</div>
							<Link href={`/releases/chromium/${release.browserMajor}`}>
								<div className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm font-medium hover:bg-accent">
									<AppWindow className="size-4 text-muted-foreground" />
									<span>Chromium {release.browserMajor}</span>
								</div>
							</Link>
						</div>
						<p className="mt-1 text-sm text-muted-foreground">
							{release.fileCount.toLocaleString()} files ·{" "}
							{formatBytes(release.sizeBytes)} ·{" "}
							{formatRelative(release.publishedAt)}
							{release.publishedBy ? ` by ${release.publishedBy}` : null}
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						<Button asChild size="sm" variant="outline">
							<a href={release.webPath} rel="noreferrer">
								<Download className="mr-1 size-3.5" /> Download tarball
							</a>
						</Button>
						{canPublish ? (
							<>
								<Button
									size="sm"
									variant={release.isActive ? "secondary" : "default"}
									disabled={release.isActive || activate.isPending}
									onClick={() => activate.mutate()}
								>
									{activate.isPending ? (
										<Loader2 className="mr-1 size-3.5 animate-spin" />
									) : release.isActive ? (
										<CheckCircle2 className="mr-1 size-3.5" />
									) : null}
									{release.isActive ? "Active" : "Activate"}
								</Button>
								<Button
									size="sm"
									variant="ghost"
									aria-label="Delete release"
									disabled={remove.isPending}
									onClick={() => setDeleteOpen(true)}
								>
									<Trash2 className="size-3.5" />
								</Button>
							</>
						) : null}
					</div>
				</div>
			</div>

			{release.description ? (
				<Card size="sm">
					<CardContent>
						<p className="text-sm">{release.description}</p>
					</CardContent>
				</Card>
			) : null}

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				<Card>
					<CardHeader>
						<CardDescription>Files</CardDescription>
						<CardTitle className="font-mono text-2xl tabular-nums">
							{release.fileCount.toLocaleString()}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="text-xs text-muted-foreground">
							Published <code className="font-mono">.enc</code> files in{" "}
							<code className="font-mono">profiles.tar.gz</code>
						</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardDescription>Size</CardDescription>
						<CardTitle className="font-mono text-2xl tabular-nums">
							{formatBytes(release.sizeBytes)}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="font-mono text-xs text-muted-foreground">
							sha256:{release.sha256.slice(0, 12)}…
						</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardDescription>Installed on workers</CardDescription>
						<CardTitle className="font-mono text-2xl tabular-nums">
							{installedHosts.length}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{installedHosts.length === 0 ? (
							<p className="text-xs text-muted-foreground">
								No worker has installed this exact bundle (version+slug) yet.
							</p>
						) : (
							<div className="flex flex-wrap gap-1">
								{installedHosts.map((h) => (
									<Badge
										key={h}
										variant="outline"
										className="font-mono text-[10px]"
									>
										{h}
									</Badge>
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{breakdownMajor ? (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Bundle breakdown</CardTitle>
						<CardDescription>
							Captured from the source raw bundle{" "}
							{release.rawBundleFilename ? (
								<code className="font-mono">{release.rawBundleFilename}</code>
							) : null}
							{matchingMajor &&
							matchingMajor.fileCount !== release.fileCount ? (
								<span>
									{" "}
									· OS counts and file total from published release (
									{release.fileCount.toLocaleString()} files vs.{" "}
									{matchingMajor.fileCount.toLocaleString()} detected in raw
									bundle)
								</span>
							) : null}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<MajorBreakdown major={breakdownMajor} />
					</CardContent>
				</Card>
			) : (
				<Card>
					<CardHeader>
						<CardTitle className="text-base">OS distribution</CardTitle>
						<CardDescription>
							{release.inspectionError
								? `Inspection error: ${release.inspectionError}`
								: "Source bundle is no longer available; showing the per-release OS summary."}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<OsRollup oses={release.oses} />
					</CardContent>
				</Card>
			)}

			<div>
				<Button asChild variant="ghost" size="sm">
					<Link href="/releases/profiles">
						<ChevronLeft className="mr-1 size-3.5" /> Back to profiles
					</Link>
				</Button>
			</div>

			{deleteOpen ? (
				<Suspense fallback={null}>
					<DeleteReleaseDialog
						open
						onOpenChange={setDeleteOpen}
						release={release}
						fleetHostsInstalled={installedHosts}
						confirming={remove.isPending}
						onConfirm={(force) => remove.mutate(force)}
					/>
				</Suspense>
			) : null}
		</div>
	);
}

function OsRollup({
	oses,
}: {
	oses: { win: number; mac: number; linux: number; android: number };
}) {
	const total = oses.win + oses.mac + oses.linux + oses.android;
	const rows: Array<[string, number]> = [
		["Windows", oses.win],
		["Android", oses.android],
		["macOS", oses.mac],
		["Linux", oses.linux],
	];
	if (total === 0) {
		return (
			<p className="text-xs text-muted-foreground">
				No OS counts recorded for this release.
			</p>
		);
	}
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>OS</TableHead>
					<TableHead className="text-right">Files</TableHead>
					<TableHead className="text-right">%</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map(([label, count]) => (
					<TableRow key={label}>
						<TableCell>{label}</TableCell>
						<TableCell className="text-right font-mono tabular-nums">
							{count.toLocaleString()}
						</TableCell>
						<TableCell className="text-right font-mono tabular-nums text-muted-foreground">
							{Math.round((count / total) * 100)}%
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
